/**
 * Transformation to the Fay-Herriot scale (brief 9.5, MET-04), implemented
 * behind the transformDomains and transformAreaLevelDomains stubs in
 * engine.ts.
 *
 * Arcsine (default): thetahat_d = asin(sqrt(phat_d)), ranging over
 * [0, pi/2], and psi_d = 1 / (4 * neff_d) with neff_d = n_d / deff_c. The
 * sampling variance depends only on the effective sample size, so domains
 * with phat_d of 0 or 1 stay in the model with valid variances (brief 9.5)
 * — their psi comes from the effective sample size (inputs.ts).
 *
 * Workstream F-1 (design-effect method 'area-direct', Decision Log
 * Addendum 56; construction as registered in
 * validation/simulation/results/FRONTIER_F1_PSI_RESULTS.md): the
 * per-domain design effect in place of deff_c is the blend
 * deff_d^F1 = w_d x deff_d^step + (1 - w_d) x deff_c^P2, where
 * deff_d^step is the area's own stepwise design effect (its
 * per-domain stepwise first-stage-FPC variance over its SRS
 * benchmark — the same Vhat_d^FPC candidate W threaded),
 * deff_c^P2 is the cell value smoothDesignEffects returns under
 * this method (the shipped 'state' basis value itself), and
 * w_d = s_d / (s_d + K) rises with the domain's support s_d,
 * its member PSU count as the direct stage computes it (the
 * support measure as amended by the study's measurement-phase
 * iteration 1: the design degrees of freedom are identically
 * zero on one-PSU-per-stratum designs). By the delta relation
 * the blend is a psi-level blend on both scales:
 * psi_d = w_d x psi_d^own + (1 - w_d) x psi_d^P2, with
 * psi_d^own = Vhat_d^FPC / (4 phat_d (1 - phat_d)) on the
 * arcsine scale and Vhat_d^FPC on the linear scale. Where the
 * area's own stepwise design effect is undefined — no frame
 * counts, a boundary phat_d, or a stepwise variance of zero —
 * w_d = 0 and the domain reads its shipped P2 psi exactly; with
 * no frame counts anywhere the method reproduces the shipped P2
 * run bit-for-bit. The brief 9.4
 * [min, max] clamp applies to deff_c^P2 as shipped and not to
 * deff_d^F1. TransformedDomain.designEffect records deff_d^F1.
 *
 * Candidate W (design-effect method 'within-area', Decision Log
 * Addendum 50 Item 1; construction as registered in
 * validation/simulation/results/TIER2_W_BASIS_RESULTS.md, iteration
 * 2): the per-domain factor in place of deff_c is deff_w x FPC_d,
 * where deff_w is the cell's pooled within-area design effect
 * (deff.ts, pooled over the domains' stepwise first-stage-FPC
 * variances when frame counts were supplied) and FPC_d is the
 * area's own first-stage factor: the area's stepwise design effect
 * deff_d = fpcVariance_d / (phat_d (1 - phat_d) / n_d), relative to
 * deff_w, bounded to [1/3, 3] as the registered stability rule. A
 * domain with no usable stepwise variance (no frame counts, a
 * boundary phat_d, or a stepwise variance of 0) reads FPC_d = 1.
 * psi_d = deff_w x FPC_d / (4 n_d) on this scale; the linear analog
 * multiplies the same factor into deff_c's place.
 * TransformedDomain.designEffect records the per-domain factor
 * actually used.
 *
 * Linear (Expert): thetahat_d = phat_d. The brief defines psi_d through
 * the synthetic prediction of a first-pass fit (a two-pass GVF); the
 * transformDomains signature receives no covariates or fit, so that form
 * is inexpressible here and the one-pass analogue
 * psi_d = deff_c * phat_d * (1 - phat_d) / n_d is used (see
 * validation/parity/OPEN_QUESTIONS.md Q8). On the area-level path the
 * published sampling variance is the linear-scale psi directly.
 *
 * The two-pass GVF, specified completely (directive C-3 — the brief
 * names the construction but does not pin these three choices):
 * - First-pass psi: psi_d^(1) = deff_c * phat_d * (1 - phat_d) / n_d,
 *   the one-pass value above. The first pass fits the Fay-Herriot
 *   model with thetahat_d = phat_d and psi^(1).
 * - Synthetic prediction and clamping: ptilde_d = x_d' betahat^(1),
 *   the first-pass synthetic (regression) prediction, clamped to
 *   [1 / (2 * n_d), 1 - 1 / (2 * n_d)] before use — the half-count
 *   clamp, the form already adopted for the transform mean-bias
 *   correction (Addendum 16). A synthetic prediction outside (0, 1)
 *   — possible whenever the linear predictor extrapolates — would
 *   otherwise make psi_d zero or negative; the clamp keeps
 *   deff_c * (1 / (2 * n_d)) * (1 - 1 / (2 * n_d)) / n_d <= psi_d
 *   <= deff_c / (4 * n_d). CLAMP HISTORY: this clamp was originally
 *   specified as the epsilon clamp [1e-6, 1 - 1e-6] (the same
 *   clamp the benchmarking logit shift applies). Tier 2 Item 7
 *   measured the two clamps paired on the recorded E-2/E-3
 *   designs (validation/simulation/results/
 *   TIER2_ITEM7_RESULTS.md): the epsilon clamp leaves
 *   psi_d ~ deff_c * 1e-6 / n_d — near zero, giving the area
 *   near-infinite weight in the refit — whenever the synthetic
 *   prediction lands on a boundary, and the half-count clamp
 *   showed no degradation in aggregate bias, coverage, or
 *   reported-to-realized RMSE ratios (pooled coverage improved
 *   on all three designs), so the specification carries the
 *   half-count clamp from 2026-10-20 under the item's gate.
 * - Second-pass psi and iteration rule: psi_d = deff_c * ptilde_d *
 *   (1 - ptilde_d) / n_d, and the model is refitted ONCE with these
 *   psi. The procedure does not iterate: the second-pass fit's own
 *   synthetic predictions are not fed back into psi. Two passes are
 *   the whole GVF; iterating to a fixed point would make each area's
 *   sampling variance a function of the final fit and is not the
 *   brief's construction.
 * Status: the specification above is the brief's form, recorded here
 * and in Q8 under directive C-3. The shipped engine implements the
 * one-pass analogue (the first-pass psi at every stage), because
 * transformDomains receives no covariates; implementing the second
 * pass belongs in the analysis layer and remains a logged deviation
 * (Q8), not a silent one. A study implementation of the second
 * pass exists in the analysis layer behind the study-only
 * ModelConfig.studyGvfClamp switch (Tier 2 Item 7, the clamp
 * measurement above); it is not shipped behavior, and the shipped
 * default is unchanged by the clamp decision.
 *
 * transformDomains skips the statewide domains computeDirectEstimates
 * emits: they are design-effect and benchmarking inputs, not model areas.
 *
 * Transform mean-bias correction (decision D-3 part 2, 2026-10-17;
 * validation in docs/CORRECTION_VALIDATION.md): on the arcsine
 * scale the transformed direct estimate is mean-biased at first
 * order. With g(p) = asin(sqrt(p)) and the design variance of phat
 * written p (1 - p) / n_eff,
 *   E[g(phat)] - g(p) ~= (1/2) g''(p) Var(phat)
 *                     = -(1 - 2p) / (8 n_eff sqrt(p (1 - p))).
 * When options.meanBiasCorrection is on, thetahat is shifted by the
 * plug-in estimate of minus that bias,
 *   c(phat, n_eff) = (1 - 2 q) / (8 n_eff sqrt(q (1 - q))),
 *   q = clamp(phat, 1 / (2 n_eff), 1 - 1 / (2 n_eff)),
 * where the half-count clamp is the boundary rule: the raw plug-in
 * diverges at phat in {0, 1} (its expectation is not even finite),
 * and q is the proportion entering the plug-in, resolved no finer
 * than half a count on the n_eff grid (the same "keep plug-in
 * proportions off the boundary" convention the benchmarking logit
 * shift and the GVF clamp apply, at the count scale instead of a
 * fixed epsilon). At n_eff <= 1 the clamp reaches 1/2 and the term
 * vanishes. psi is NOT altered: the correction is a location shift
 * of the input, and psi remains the sampling variance of the
 * transformed direct estimate.
 *
 * The n_eff in the term is the domain's own effective sample size
 * under the run's design-effect basis — the same n_eff psi is built
 * from. That pairing is part of the formula's meaning, not an
 * implementation detail: the first-order bias is (1/2) g''(p) times
 * the TRUE Var(phat), and n_eff is the pipeline's estimate of
 * p (1 - p) / Var(phat). Study E-4 measured the shipped statewide
 * basis overstating Var(phat) by about 1.7x (calibration 0.60),
 * which miscalibrates any first-order term read off it; the pooled
 * basis calibrated at 1.03. Gate A (validation/simulation,
 * results/GATEA_RESULTS.md) measured the corrected input bias on
 * both recorded designs for the formula x basis grid: only the
 * delta term on the pooled basis passes (|z| <= 2 on both designs).
 * The linear transform is the identity, whose second derivative is
 * zero: it takes no correction under any setting.
 */
import { STATEWIDE_AREA_ID } from './direct.js';
import { NonProportionEstimateError } from './errors.js';
import { clamp, logGamma } from './numeric.js';
/**
 * The first-order mean-bias correction added to an arcsine
 * thetahat (decision D-3 part 2; derivation in this module's header
 * and docs/CORRECTION_VALIDATION.md). Zero when the effective
 * sample size is not positive.
 */
export function arcsineMeanBiasCorrection(phat, effectiveSampleSize) {
    const neff = effectiveSampleSize;
    if (!(neff > 0))
        return 0;
    const lo = Math.min(1 / (2 * neff), 0.5);
    const q = clamp(phat, lo, 1 - lo);
    return (1 - 2 * q) / (8 * neff * Math.sqrt(q * (1 - q)));
}
/**
 * E[asin(sqrt(X / n))] for X ~ Bin(n, q), integer n >= 1, computed by
 * direct summation over the binomial support (Tier 2 Item 1). The
 * sum runs outward from the mode by the pmf recurrence; past the
 * mode each direction's terms decrease geometrically, and the
 * recurrence stops once a term falls below 1e-17, which bounds the
 * omitted tail's contribution to the expectation below ~1e-15
 * (terms are at most pi/2 and the omitted mass is of the order of
 * the stopping term). At integer n the result is the exact
 * expectation up to that summation tolerance.
 */
function arcsineBinomialExpectation(q, n) {
    if (q <= 0)
        return 0;
    if (q >= 1)
        return Math.PI / 2;
    const mode = Math.min(n, Math.max(0, Math.floor((n + 1) * q)));
    const logPmfMode = logGamma(n + 1) -
        logGamma(mode + 1) -
        logGamma(n - mode + 1) +
        (mode > 0 ? mode * Math.log(q) : 0) +
        (n - mode > 0 ? (n - mode) * Math.log(1 - q) : 0);
    const pmfMode = Math.exp(logPmfMode);
    let sum = pmfMode * Math.asin(Math.sqrt(mode / n));
    let pmf = pmfMode;
    for (let x = mode; x < n; x++) {
        pmf *= ((n - x) / (x + 1)) * (q / (1 - q));
        sum += pmf * Math.asin(Math.sqrt((x + 1) / n));
        if (pmf < 1e-17)
            break;
    }
    pmf = pmfMode;
    for (let x = mode; x > 0; x--) {
        pmf *= (x / (n - x + 1)) * ((1 - q) / q);
        sum += pmf * Math.asin(Math.sqrt((x - 1) / n));
        if (pmf < 1e-17)
            break;
    }
    return sum;
}
/**
 * The exact variance of asin(sqrt(K / n)) for K ~ Binomial(n, p)
 * (candidate V, Path to A guide Section 2.2): V(n, p) =
 * E[asin^2(sqrt(K / n))] - (E[asin(sqrt(K / n))])^2, computed by
 * direct summation over the binomial support. Each pmf is evaluated
 * in log space (the logGamma form arcsineBinomialExpectation above
 * uses); no tail is omitted. The sum is accumulated in the centered
 * form sum_k pmf_k (a_k - E[a])^2 with compensated (Kahan)
 * summation — algebraically the definition above, but the
 * uncentered difference of two O(1) quantities cancels
 * catastrophically at large n (the variance is O(1 / n) while both
 * moments are O(1)), and the centered form is what makes the
 * function agree with an independent direct summation to the
 * 1e-12 relative tolerance the guide's verification requires
 * (measured in validation/simulation/results/
 * PATHA_V_VERIFICATION.md).
 *
 * Non-integer n: V is computed at floor(n) and ceil(n), the product
 * n * V(n, p) is interpolated linearly between those two integer
 * points, and the result is divided by n (the guide's construction,
 * exactly). Above n = 2000 the guide's rule applies and the
 * function returns 1 / (4 n); n = 2000 itself is computed exactly.
 * p at a boundary (0 or 1) gives variance 0. n must be at least 1.
 */
export function exactBinomialArcsineVariance(n, p) {
    if (!Number.isFinite(n) || !(n >= 1)) {
        throw new Error(`exactBinomialArcsineVariance: n must be finite and at least 1; received ${n}.`);
    }
    if (!Number.isFinite(p) || p < 0 || p > 1) {
        throw new Error(`exactBinomialArcsineVariance: p must lie in [0, 1]; received ${p}.`);
    }
    if (p === 0 || p === 1)
        return 0;
    if (n > 2000)
        return 1 / (4 * n);
    if (Number.isInteger(n))
        return exactBinomialArcsineVarianceInteger(n, p);
    const lo = Math.floor(n);
    const hi = Math.ceil(n);
    const vLo = exactBinomialArcsineVarianceInteger(lo, p);
    const vHi = exactBinomialArcsineVarianceInteger(hi, p);
    // Linear interpolation of the product n * V(n, p) between lo and hi.
    const product = (hi - n) * (lo * vLo) + (n - lo) * (hi * vHi);
    return product / n;
}
/** Integer-n core of exactBinomialArcsineVariance (direct log-space summation). */
function exactBinomialArcsineVarianceInteger(n, p) {
    const logP = Math.log(p);
    const logQ = Math.log(1 - p);
    const logChooseTotal = logGamma(n + 1);
    const pmfs = new Array(n + 1);
    const angles = new Array(n + 1);
    for (let k = 0; k <= n; k++) {
        pmfs[k] = Math.exp(logChooseTotal -
            logGamma(k + 1) -
            logGamma(n - k + 1) +
            (k > 0 ? k * logP : 0) +
            (n - k > 0 ? (n - k) * logQ : 0));
        angles[k] = Math.asin(Math.sqrt(k / n));
    }
    // Compensated (Kahan) summation, centered form (see the header).
    let mean = 0;
    let comp = 0;
    for (let k = 0; k <= n; k++) {
        const y = pmfs[k] * angles[k] - comp;
        const t = mean + y;
        comp = t - mean - y;
        mean = t;
    }
    let v = 0;
    comp = 0;
    for (let k = 0; k <= n; k++) {
        const d = angles[k] - mean;
        const y = pmfs[k] * d * d - comp;
        const t = v + y;
        comp = t - v - y;
        v = t;
    }
    return v > 0 ? v : 0;
}
/**
 * The exact-expectation form of the arcsine mean-bias correction
 * (Tier 2 Item 1, measured against the shipped delta plug-in above;
 * study-only — selected by
 * ModelConfig.studyTransformBiasCorrectionForm, never set by the
 * app). The plug-in replaces the term's FORM, nothing else: the
 * quantity the delta term approximates,
 *   B(p, n_eff) = E[asin(sqrt(phat))] - asin(sqrt(p)),
 * is computed by direct binomial summation instead of the
 * first-order expansion, and the correction added to thetahat is
 * -B evaluated at the same clamped plug-in proportion q the delta
 * term reads (same half-count clamp, same n_eff, so at
 * n_eff <= 1 the clamp reaches 1/2 and the term vanishes, exactly
 * as the plug-in's does). The binomial at the effective count is
 * the distribution Gate A's pre-wiring verification used: it is
 * the law of phat with the mean and variance the pipeline itself
 * assumes (p and p (1 - p) / n_eff). n_eff is generally not an
 * integer in the pipeline; the exact bias is interpolated linearly
 * in n between the two bracketing integer counts, which is exact
 * at integer n_eff and keeps the term continuous in n_eff. psi is
 * not altered, as with the plug-in. The linear transform takes no
 * correction under either form.
 */
export function arcsineExactExpectationCorrection(phat, effectiveSampleSize) {
    const neff = effectiveSampleSize;
    if (!(neff > 0))
        return 0;
    const lo = Math.min(1 / (2 * neff), 0.5);
    const q = clamp(phat, lo, 1 - lo);
    if (q === 0.5)
        return 0;
    // neff > 1 here: at neff <= 1 the clamp above forces q = 1/2.
    const nLo = Math.floor(neff);
    const frac = neff - nLo;
    const eLo = arcsineBinomialExpectation(q, nLo);
    const expected = frac === 0
        ? eLo
        : (1 - frac) * eLo + frac * arcsineBinomialExpectation(q, nLo + 1);
    return Math.asin(Math.sqrt(q)) - expected;
}
/** The correction term an options object selects (delta by default). */
function selectedCorrection(options) {
    return options.meanBiasCorrectionForm === 'exact-expectation'
        ? arcsineExactExpectationCorrection
        : arcsineMeanBiasCorrection;
}
/**
 * Registered stability bound for the area first-stage factor under
 * candidate W (TIER2_W_BASIS_RESULTS.md, iteration 2): FPC_d, a
 * ratio of two design-effect-scale quantities, is bounded to
 * [1/3, 3] (log-symmetric). This is not the brief 9.4 [1, 5] clamp —
 * it does not apply to deff_w or to the product deff_w x FPC_d, and
 * it preserves the factor's full downward action to 1/3 (the
 * near-census areas) and upward action to 3; it trims only the
 * estimator explosions a per-area variance ratio produces (a
 * domain whose with-replacement variance collapses to ~0 while its
 * stepwise variance is positive, the certainty-strata case).
 */
export const WITHIN_AREA_FPC_MIN = 1 / 3;
export const WITHIN_AREA_FPC_MAX = 3;
/**
 * The area's own first-stage factor FPC_d under candidate W: the
 * area's stepwise design effect (its stepwise first-stage-FPC
 * variance over its SRS benchmark) relative to the cell's pooled
 * within-area design effect deff_w, bounded to
 * [WITHIN_AREA_FPC_MIN, WITHIN_AREA_FPC_MAX]. On a one-stratum
 * design with no certainty strata this reduces to (1 - f_d) up to
 * the pooling normalization — the reviewer's arm-C factor. 1 when
 * no usable stepwise variance exists: no frame counts supplied, a
 * boundary phat_d (the domain design effect is undefined there, as
 * in deff.ts), or a stepwise variance of exactly 0 (the registered
 * stability rule of iteration 1: a thin domain whose
 * certainty-stratum second-stage contributions sum to 0 has no
 * estimable first-stage factor and is treated as with-replacement).
 */
/**
 * The shrinkage constant K of the 'area-direct' design-effect
 * method (workstream F-1, Decision Log Addendum 56; registration
 * in validation/simulation/results/FRONTIER_F1_PSI_RESULTS.md):
 * the per-domain weight on the area's own stepwise design effect
 * is w_d = s_d / (s_d + K), s_d the domain's member PSU count.
 * K = 4 is the frozen constant of the construction, fixed by
 * the F-1 measurement freeze of 2026-10-20 under the registered
 * K-selection rule (the smallest grid value whose recorded
 * coverage lay in [0.92, 0.97] at E-3 overall, E-2 logit, and
 * E-2 arcsine while the recorded A3(d) draws beat the P2 arm's
 * coverage with a model-versus-direct RMSE ratio at most 1;
 * FRONTIER_F1_PSI_RESULTS.md Section 3). TransformOptions.
 * areaDirectK / ModelConfig.deff.areaDirectK override it per run
 * (the study harness's measurement scans; the app never sets it).
 */
export const AREA_DIRECT_SHRINKAGE_K = 4;
/**
 * The per-domain design effect under workstream F-1 (method
 * 'area-direct'): deff_d^F1 = w_d x deff_d^step + (1 - w_d) x
 * deffP2, deff_d^step the area's own stepwise design effect (its
 * stepwise first-stage-FPC variance over its SRS benchmark),
 * deffP2 the cell's shipped P2 value, and w_d = s_d / (s_d + K)
 * with s_d the domain's support, its member PSU count as the
 * direct stage computes it (support measure as amended by the
 * study's measurement-phase iteration 1). Returns deffP2
 * unchanged — weight zero — whenever the area's own stepwise
 * design effect is undefined: no stepwise variance (no frame
 * counts supplied), a non-finite or zero stepwise variance (a
 * zero carries no first-stage information; the stability rule
 * of candidate W's iteration 1), or a boundary phat_d (the
 * domain design effect is undefined there, as in deff.ts). A
 * domain with no member PSUs is never emitted by the direct
 * stage, so the formula needs no zero-support rule.
 */
export function areaDirectDeff(est, deffP2, k) {
    if (est.fpcVariance === undefined)
        return deffP2;
    if (!(est.estimate > 0 && est.estimate < 1))
        return deffP2;
    if (!(est.n > 0) || !(deffP2 > 0))
        return deffP2;
    if (!Number.isFinite(est.fpcVariance) || !(est.fpcVariance > 0)) {
        return deffP2;
    }
    const support = est.psuCount;
    if (!(support > 0))
        return deffP2;
    const deffStep = est.fpcVariance / ((est.estimate * (1 - est.estimate)) / est.n);
    if (!Number.isFinite(deffStep) || !(deffStep > 0))
        return deffP2;
    const w = support / (support + k);
    return w * deffStep + (1 - w) * deffP2;
}
function areaFpcFactor(est, deffW) {
    if (est.fpcVariance === undefined)
        return 1;
    if (!(est.estimate > 0 && est.estimate < 1))
        return 1;
    if (!(est.n > 0) || !(deffW > 0))
        return 1;
    if (!Number.isFinite(est.fpcVariance) || !(est.fpcVariance > 0))
        return 1;
    const deffD = est.fpcVariance / ((est.estimate * (1 - est.estimate)) / est.n);
    if (!Number.isFinite(deffD))
        return 1;
    return clamp(deffD / deffW, WITHIN_AREA_FPC_MIN, WITHIN_AREA_FPC_MAX);
}
export function transformDomainsImpl(estimates, cellDesignEffects, options) {
    const deffByCell = new Map();
    for (const cde of cellDesignEffects)
        deffByCell.set(cde.cellId, cde);
    const out = [];
    for (const est of estimates) {
        if (est.domain.areaId === STATEWIDE_AREA_ID)
            continue;
        const cde = deffByCell.get(est.domain.cellId);
        if (cde === undefined) {
            throw new Error(`transformDomains: no design effect supplied for cell ${est.domain.cellId}`);
        }
        if (!(est.n > 0)) {
            throw new Error(`transformDomains: domain ${est.domain.cellId}/${est.domain.areaId} has no respondents`);
        }
        // Candidate W (method 'within-area'): the per-domain factor is
        // deff_w x FPC_d. Workstream F-1 (method 'area-direct'): the
        // per-domain factor is the stabilized blend of the area's own
        // stepwise design effect with the cell's P2 value (see
        // areaDirectDeff). Every other method uses the cell scalar as
        // shipped.
        const deff = cde.method === 'within-area'
            ? cde.designEffect * areaFpcFactor(est, cde.designEffect)
            : cde.method === 'area-direct'
                ? areaDirectDeff(est, cde.designEffect, options.areaDirectK ?? AREA_DIRECT_SHRINKAGE_K)
                : cde.designEffect;
        const neff = est.n / deff;
        let thetaHat;
        let psi;
        if (options.transform === 'arcsin') {
            thetaHat = Math.asin(Math.sqrt(clamp(est.estimate, 0, 1)));
            if (options.meanBiasCorrection === true) {
                thetaHat += selectedCorrection(options)(est.estimate, neff);
            }
            psi = 1 / (4 * neff);
        }
        else {
            thetaHat = est.estimate;
            psi = (deff * est.estimate * (1 - est.estimate)) / est.n;
        }
        out.push({
            domain: est.domain,
            n: est.n,
            effectiveSampleSize: neff,
            designEffect: deff,
            thetaHat,
            psi,
        });
    }
    return out;
}
export function transformAreaLevelDomainsImpl(input, options) {
    // Proportion-scale contract (Tier 3 verdict, 2026-11-01, Section 3):
    // a published direct estimate outside [0, 1] is a natural-scale
    // input, and the path refuses it here — before any transformation
    // or estimation — with a plain message. Before this guard the
    // arcsine branch clamped such an estimate to 1 and the linear
    // branch carried it through to a back-transformation clamped to
    // [0, 1], so the path published 1.000 / [0, 1] for natural-scale
    // inputs with no refusal and no error (fixes-round probe,
    // docs/VERDICT_FIXES_RECORD.md).
    const offenders = input.domains
        .filter((d) => !Number.isFinite(d.directEstimate) ||
        d.directEstimate < 0 ||
        d.directEstimate > 1)
        .map((d) => ({ areaId: String(d.areaId), value: d.directEstimate }));
    if (offenders.length > 0) {
        throw new NonProportionEstimateError(offenders);
    }
    // Canonical covariate order: the key order of the first covariate row.
    // Every row must carry the same covariates (brief ING-03, QC-13).
    const covariateNames = input.covariates.length > 0 ? Object.keys(input.covariates[0].values) : [];
    const covariateVector = (areaId) => {
        // Intercept-only configuration: the input carries no covariate
        // rows at all (no covariate columns were assigned), so every
        // domain's design row is the intercept alone. Step 2 offers this
        // configuration in words ("No covariate candidates. The model
        // will fit an intercept-only model") and an intercept-only
        // Fay-Herriot fit is well-defined, so the offered configuration
        // fits. Before Addendum 189 the lookup below ran unconditionally
        // and threw for every domain, so a no-covariate run could not
        // run at all (docs/VERDICT_FIXES_RECORD.md, walk section B).
        if (input.covariates.length === 0)
            return [1];
        const row = input.covariates.find((r) => r.areaId === areaId);
        if (!row) {
            throw new Error(`No covariate values were supplied for area ${areaId}. Every area in the model needs a row of covariate values; check that the covariate columns cover every area, or remove the covariate columns to fit an intercept-only model.`);
        }
        return [
            1,
            ...covariateNames.map((name) => {
                const value = row.values[name];
                if (value === undefined || !Number.isFinite(value)) {
                    throw new Error(`The covariate "${name}" has no usable value for area ${areaId}. Check the covariate column for missing or non-numeric entries.`);
                }
                return value;
            }),
        ];
    };
    for (const row of input.covariates) {
        const keys = Object.keys(row.values);
        if (keys.length !== covariateNames.length ||
            keys.some((k) => !covariateNames.includes(k))) {
            throw new Error(`The covariate values for area ${row.areaId} do not match the model's covariate set. Check that every area's row carries the same covariates.`);
        }
    }
    const out = [];
    const domainAreas = new Set();
    for (const domain of input.domains) {
        domainAreas.add(domain.areaId);
        let thetaHat;
        let psi;
        if (options.transform === 'arcsin') {
            if (!(domain.sampleSize > 0)) {
                throw new Error(`Area ${domain.areaId} has a sample size of 0, so its effective sample size cannot be computed. Give the area a sample size above 0 or remove it from the file.`);
            }
            const neff = domain.sampleSize / options.designEffect;
            thetaHat = Math.asin(Math.sqrt(clamp(domain.directEstimate, 0, 1)));
            if (options.meanBiasCorrection === true) {
                thetaHat += selectedCorrection(options)(domain.directEstimate, neff);
            }
            psi = 1 / (4 * neff);
        }
        else {
            thetaHat = domain.directEstimate;
            psi = domain.samplingVariance;
        }
        out.push({
            areaId: domain.areaId,
            thetaHat,
            psi,
            covariates: covariateVector(domain.areaId),
        });
    }
    // Areas with covariates but no domain input are out-of-sample areas
    // (brief 9.8): the fit needs only their covariates, so thetaHat/psi are
    // inert placeholders flagged by outOfSample.
    for (const row of input.covariates) {
        if (domainAreas.has(row.areaId))
            continue;
        out.push({
            areaId: row.areaId,
            thetaHat: 0,
            psi: 0,
            covariates: covariateVector(row.areaId),
            outOfSample: true,
        });
    }
    return out;
}
//# sourceMappingURL=transform.js.map